// ─── AdminVehicles — gestão de veículos por Entidade/Organização ──────
// Fase 1 da autonomia do EcooGPS: o super-admin gere, no Planner, os veículos
// PRÓPRIOS de cada entidade (tabela `vehicles`). Para entidades EcooGPS a frota
// ainda vem em tempo real do EcooGPS; estes são a base para operar só com o
// Traccar (o "ID do dispositivo" = device id no Traccar).
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, TextField, Switch,
  FormControl, InputLabel, Select, MenuItem, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import { COLORS } from '../../theme';
import AdminHint from '../components/AdminHint';

const API_BASE = '/planner-api';
const EMPTY = { plate: '', type: '', capacity: '', externalId: '', maxWeightKg: '', maxVolumeM3: '', maxPallets: '', fuelPer100km: '', isActive: true };

const useStyles = makeStyles(() => ({
  header: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 },
  titleIcon: { width: 44, height: 44, borderRadius: 12, background: 'var(--adm-brand-soft, rgba(47,93,80,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '1.72rem', fontWeight: 800, color: COLORS.text },
  subtitle: { fontSize: '0.84rem', color: COLORS.textSecondary },
  toolbar: { display: 'flex', alignItems: 'center', gap: 14, margin: '18px 0', flexWrap: 'wrap' },
  grow: { flex: 1 },
  tableCard: { background: 'var(--adm-card, #fff)', borderRadius: 16, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)', overflow: 'visible' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '14px 18px', fontSize: '0.66rem', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'var(--adm-thead-bg, #f8fafc)', position: 'sticky', top: 0, zIndex: 2 },
  td: { padding: '13px 18px', fontSize: '0.84rem', color: COLORS.text, borderBottom: '1px solid rgba(0,0,0,0.03)' },
  row: { transition: 'background 0.15s', '&:hover': { background: 'var(--adm-hover, #f8fafc)' }, '&:last-child td': { borderBottom: 'none' } },
  plate: { fontWeight: 800, color: COLORS.text },
  muted: { color: COLORS.textMuted },
  badge: { display: 'inline-flex', padding: '3px 10px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700 },
  on: { background: '#e9f7ec', color: '#15803d' },
  off: { background: '#f1f5f9', color: '#64748b' },
  device: { fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace", fontSize: '0.76rem', color: COLORS.textSecondary },
  empty: { padding: '40px 20px', textAlign: 'center', color: COLORS.textMuted },
  loading: { display: 'flex', justifyContent: 'center', padding: 60 },
  dialogPaper: { borderRadius: 16, width: 560, maxWidth: '94vw' },
  dialogTitle: { fontWeight: 800, color: COLORS.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 4 },
  switchRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 },
  err: { color: 'var(--adm-danger, #dc2626)', fontSize: '0.8rem', marginTop: 8, fontWeight: 600 },
  newBtn: { background: 'var(--adm-brand, #2f5d50)', color: '#fff', fontWeight: 700, textTransform: 'none', borderRadius: 10, '&:hover': { background: 'var(--adm-brand-dark, #264b41)' } },
}));

function AdminVehicles({ token }) {
  const classes = useStyles();
  const auth = { Authorization: `Bearer ${token}` };
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/clients`, { headers: auth });
        if (res.ok) setClients(await res.json());
      } catch (e) { console.error('Clients load error:', e); }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  async function loadVehicles(cid) {
    if (!cid) { setVehicles([]); return; }
    setListLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${cid}/vehicles`, { headers: auth });
      setVehicles(res.ok ? await res.json() : []);
    } catch (e) { console.error('Vehicles load error:', e); setVehicles([]); }
    setListLoading(false);
  }
  function pickClient(cid) { setClientId(cid); loadVehicles(cid); }

  function openNew() { setError(''); setEditing({ ...EMPTY }); }
  function openEdit(v) {
    setError('');
    setEditing({
      ...EMPTY, ...v,
      type: v.type || '', capacity: v.capacity || '', externalId: v.externalId || '',
      maxWeightKg: v.maxWeightKg ?? '', maxVolumeM3: v.maxVolumeM3 ?? '',
      maxPallets: v.maxPallets ?? '', fuelPer100km: v.fuelPer100km ?? '',
      isActive: v.isActive !== false,
    });
  }
  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }));

  async function handleSave() {
    if (!editing.plate.trim()) { setError('Matrícula obrigatória'); return; }
    setSaving(true); setError('');
    const payload = {
      plate: editing.plate.trim(), type: editing.type || null, capacity: editing.capacity || null,
      externalId: editing.externalId || null,
      maxWeightKg: editing.maxWeightKg, maxVolumeM3: editing.maxVolumeM3,
      maxPallets: editing.maxPallets, fuelPer100km: editing.fuelPer100km,
      isActive: editing.isActive,
    };
    try {
      const url = editing.id
        ? `${API_BASE}/clients/${clientId}/vehicles/${editing.id}`
        : `${API_BASE}/clients/${clientId}/vehicles`;
      const res = await fetch(url, {
        method: editing.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(payload),
      });
      if (res.ok) { setEditing(null); await loadVehicles(clientId); }
      else { const e = await res.json().catch(() => ({})); setError(e.error || 'Erro ao guardar'); }
    } catch { setError('Erro de rede'); }
    setSaving(false);
  }

  async function handleDelete(v) {
    if (!window.confirm(`Apagar o veículo "${v.plate}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/vehicles/${v.id}`, { method: 'DELETE', headers: auth });
      if (res.ok) await loadVehicles(clientId);
      else window.alert('Não foi possível apagar');
    } catch { window.alert('Erro de rede'); }
  }

  if (loading) return <Box className={classes.loading}><CircularProgress size={36} style={{ color: 'var(--adm-brand, #2f5d50)' }} /></Box>;

  const orgLabel = (c) => c.name || c.ecoogpsClientName;

  return (
    <Box>
      <Box className={classes.header}>
        <Box className={classes.titleIcon}><LocalShippingIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 24 }} /></Box>
        <Box>
          <Typography className={classes.title}>Veículos</Typography>
          <Typography className={classes.subtitle}>Frota gerida no Planner, por Entidade/Organização</Typography>
        </Box>
      </Box>

      <AdminHint
        nodes={[{ text: 'Entidade / Organização', tone: 'green' }, { text: 'Veículos', tone: 'teal' }]}
        note="Aqui geres os veículos PRÓPRIOS do Planner de cada entidade. Em entidades que ainda usam o EcooGPS, a frota em tempo real vem do EcooGPS; esta lista é a base para a entidade passar a operar só com o Traccar. O 'ID do dispositivo' é o identificador do aparelho no Traccar (device), que liga o veículo às posições GPS." />

      <Box className={classes.toolbar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 320 }}>
          <InputLabel>Entidade / Organização</InputLabel>
          <Select label="Entidade / Organização" value={clientId} onChange={e => pickClient(e.target.value)}>
            <MenuItem value=""><em>— escolher —</em></MenuItem>
            {clients.map(c => <MenuItem key={c.id} value={c.id}>{orgLabel(c)}</MenuItem>)}
          </Select>
        </FormControl>
        <Box className={classes.grow} />
        {clientId && (
          <Button startIcon={<AddIcon />} className={classes.newBtn} onClick={openNew}>Novo veículo</Button>
        )}
      </Box>

      {!clientId ? (
        <Box className={classes.tableCard}><Typography className={classes.empty}>Escolhe uma Entidade/Organização acima para ver e gerir os seus veículos.</Typography></Box>
      ) : listLoading ? (
        <Box className={classes.loading}><CircularProgress size={30} style={{ color: 'var(--adm-brand, #2f5d50)' }} /></Box>
      ) : (
        <Box className={classes.tableCard}>
          <table className={classes.table}>
            <thead><tr>
              <th className={classes.th}>Matrícula</th>
              <th className={classes.th}>Tipo</th>
              <th className={classes.th}>Capacidade</th>
              <th className={classes.th}>ID dispositivo (Traccar)</th>
              <th className={classes.th}>Consumo</th>
              <th className={classes.th}>Estado</th>
              <th className={classes.th} style={{ textAlign: 'right' }}>Ações</th>
            </tr></thead>
            <tbody>
              {vehicles.length === 0 ? (
                <tr><td className={classes.td} colSpan={7}><Box className={classes.empty}>Esta entidade ainda não tem veículos no Planner.</Box></td></tr>
              ) : vehicles.map(v => (
                <tr key={v.id} className={classes.row}>
                  <td className={classes.td}><span className={classes.plate}>{v.plate}</span></td>
                  <td className={classes.td}>{v.type || <span className={classes.muted}>—</span>}</td>
                  <td className={classes.td}>{v.capacity || <span className={classes.muted}>—</span>}</td>
                  <td className={classes.td}>{v.externalId ? <span className={classes.device}>{v.externalId}</span> : <span className={classes.muted}>—</span>}</td>
                  <td className={classes.td}>{v.fuelPer100km != null ? `${v.fuelPer100km} L/100km` : <span className={classes.muted}>—</span>}</td>
                  <td className={classes.td}><span className={`${classes.badge} ${v.isActive ? classes.on : classes.off}`}>{v.isActive ? 'Ativo' : 'Inativo'}</span></td>
                  <td className={classes.td} style={{ textAlign: 'right' }}>
                    <IconButton size="small" onClick={() => openEdit(v)}><EditIcon style={{ fontSize: 18, color: 'var(--adm-brand, #2f5d50)' }} /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(v)}><DeleteIcon style={{ fontSize: 18, color: 'var(--adm-danger, #dc2626)' }} /></IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}

      {/* Dialog criar/editar */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} classes={{ paper: classes.dialogPaper }}>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <span>{editing?.id ? 'Editar veículo' : 'Novo veículo'}</span>
          <IconButton size="small" onClick={() => setEditing(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editing && (
            <>
              <div className={classes.grid2}>
                <TextField label="Matrícula *" value={editing.plate} onChange={e => set('plate', e.target.value)} size="small" variant="outlined" fullWidth autoFocus />
                <TextField label="ID dispositivo (Traccar)" value={editing.externalId} onChange={e => set('externalId', e.target.value)} size="small" variant="outlined" fullWidth helperText="device id no Traccar (liga ao GPS)" />
                <TextField label="Tipo" value={editing.type} onChange={e => set('type', e.target.value)} size="small" variant="outlined" fullWidth placeholder="Tractor / Rígido / Cisterna" />
                <TextField label="Capacidade" value={editing.capacity} onChange={e => set('capacity', e.target.value)} size="small" variant="outlined" fullWidth placeholder="ex.: 35 m³" />
                <TextField label="Consumo (L/100km)" value={editing.fuelPer100km} onChange={e => set('fuelPer100km', e.target.value)} size="small" variant="outlined" fullWidth type="number" />
                <TextField label="Peso máx. (kg)" value={editing.maxWeightKg} onChange={e => set('maxWeightKg', e.target.value)} size="small" variant="outlined" fullWidth type="number" />
                <TextField label="Volume máx. (m³)" value={editing.maxVolumeM3} onChange={e => set('maxVolumeM3', e.target.value)} size="small" variant="outlined" fullWidth type="number" />
                <TextField label="Paletes máx." value={editing.maxPallets} onChange={e => set('maxPallets', e.target.value)} size="small" variant="outlined" fullWidth type="number" />
              </div>
              <div className={classes.switchRow}>
                <Switch checked={!!editing.isActive} onChange={e => set('isActive', e.target.checked)} color="primary" />
                <Typography style={{ fontSize: '0.85rem', color: COLORS.text }}>Veículo ativo</Typography>
              </div>
              {error && <div className={classes.err}>{error}</div>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} style={{ textTransform: 'none', color: COLORS.textSecondary }}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className={classes.newBtn}>{saving ? 'A guardar…' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminVehicles;
